import EnumBadge from "../../components/ui/EnumBadge";
import Button from "../../components/ui/Button";
import Select from "../../components/ui/Select";
import Can from "../../compoonents/Can";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "../PurchaseOrderDetailsPage/PurchaseOrderDetailsPage.css";
import DeleteConfirmationModal from "../../compoonents/DeleteConfirmationModal/DeleteConfirmationModal";
import {
  TRACKING_STATUS_OPTIONS,
  formatTrackingStatus,
  getTrackingStatusColor,
} from "../../constants/trackingStatuses";
import {
  deleteTrackingById,
  getTrackingById,
  updateTrackingStatus,
} from "../../services/trackingService";
import { TrackingItem, getTrackingUserName } from "../../types/tracking";

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`status-badge ${getTrackingStatusColor(status)}`}
    style={{ marginLeft: "12px", fontSize: "14px" }}
  >
    {formatTrackingStatus(status)}
  </span>
);

const TrackingDetailsPage: React.FC = () => {
  const { trackingId } = useParams<{ trackingId: string }>();
  const navigate = useNavigate();

  const [tracking, setTracking] = useState<TrackingItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!trackingId) {
      setError("No tracking ID provided.");
      setLoading(false);
      return;
    }

    const fetchTracking = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTrackingById(trackingId);
        setTracking(data);
      } catch {
        setError("Failed to fetch tracking details.");
      } finally {
        setLoading(false);
      }
    };

    fetchTracking();
  }, [trackingId]);

  const handleStatusChange = async (newStatus: string) => {
    if (!tracking?.id) return;
    setIsUpdatingStatus(true);
    try {
      await updateTrackingStatus(tracking.id, newStatus);
      setTracking((prev) =>
        prev?.shipment
          ? {
              ...prev,
              shipment: { ...prev.shipment, shipmentStatus: newStatus },
            }
          : prev,
      );
      toast.success("Tracking status updated");
    } catch {
      toast.error("Failed to update tracking status");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleDelete = async () => {
    if (!tracking?.id) return;
    setIsDeleting(true);
    try {
      await deleteTrackingById(tracking.id);
      toast.success("Tracking deleted");
      navigate("/tracking");
    } catch {
      toast.error("Failed to delete tracking");
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="po-details-not-found">
        <h2>Loading tracking...</h2>
      </div>
    );
  }

  if (error || !tracking) {
    return (
      <div className="po-details-not-found">
        <h2>{error || "Tracking not found"}</h2>
      </div>
    );
  }

  const status = tracking.shipment?.shipmentStatus || "pending";
  const customerName = getTrackingUserName(tracking.user);

  return (
    <>

      <div className="po-details-page">
        <div className="po-details-header">
          <div className="customer-info">
            <h2 className="ttiimm">
              <span
                onClick={() => navigate("/tracking")}
                className="back-link"
                style={{ cursor: "pointer" }}
              >
                Tracking Management
              </span>{" "}
              / Details
            </h2>
            <h1>
              {customerName}
              <StatusBadge status={status} />
            </h1>
            {tracking.user?.email && (
              <p style={{ margin: "8px 0 0", color: "#666", fontSize: "14px" }}>
                {tracking.user.email}
              </p>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-end",
            }}
          >
            <label style={{ fontSize: "12px", color: "#666" }}>Status</label>
            <Can permission="tracking.manage" readOnly><Select
              value={status}
              disabled={isUpdatingStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                minWidth: "220px",
              }}
            >
              {TRACKING_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select></Can>
            <Can permission="tracking.manage"><Button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              style={{
                marginTop: "8px",
                padding: "8px 16px",
                background: "#dc2626",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
              }}
            >
              Delete Tracking
            </Button></Can>
          </div>
        </div>

        <div className="cart-item-card">
          <div className="card-grid">
            <div>
              <p className="label">Tracking ID</p>
              <p className="value">{tracking.trackingId || "—"}</p>
            </div>
            <div>
              <p className="label">Shipment ID</p>
              <p className="value">{tracking.shipmentId || "—"}</p>
            </div>
            <div>
              <p className="label">Delivery Type</p>
              <p className="value">
                <EnumBadge value={tracking.shipment?.deliveryTypeTo} />
              </p>
            </div>
            <div>
              <p className="label">Created</p>
              <p className="value">
                {tracking.createdAt
                  ? new Date(tracking.createdAt).toLocaleString()
                  : tracking.shipment?.createdAt
                    ? new Date(tracking.shipment.createdAt).toLocaleString()
                    : "—"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteConfirmationModal
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          isDeleting={isDeleting}
          itemName={tracking.trackingId || tracking.id}
        />
      )}
    </>
  );
};

export default TrackingDetailsPage;
